import { GenericForm } from '@neo/blocks/forms/generic-form'
import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { Alert } from '@neo/primitives/alert'
import { Card } from '@neo/primitives/card'
import { useRef } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { Link, Navigate } from 'router-v6'
import { RECAPTCHA_SITE_KEY } from 'v2/configs/constants'
import { getDefaultRoute } from 'v2/configs/routes'
import { FormSubmitButton } from 'v2/shared/components/form-submit-button'
import { useAuth } from 'v2/shared/hooks/use-auth'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { z } from 'zod'
import { AuthLayout } from '../../shared/layouts/auth'
import {
  AUTH_INPUT_CLASS,
  AUTH_LINK_CLASS,
  AUTH_PRIMARY_BUTTON_CLASS,
} from './components/auth-styles'
import { useResendActivationEmailMutation } from './queries'

// Fallback only — the success copy normally comes from the backend's generic
// 200 message, the single source of the anti-enumeration wording. Kept
// identical to hodor's string so the fallback is invisible.
const GENERIC_SUCCESS_COPY =
  "If this account is pending activation, we've sent a new activation email. Please check your inbox."

interface ResendActivationFormData {
  email: string
}

const fields: GenericFieldConfig<ResendActivationFormData> = [
  {
    name: 'email',
    componentType: 'input',
    zodValidation: z.string().email('Invalid email').min(1, 'Email is required'),
    label: 'Registered Email',
    inputProps: {
      placeholder: 'jane@company.com',
      inputClassName: AUTH_INPUT_CLASS,
      autoComplete: 'username',
      'data-testid': 'input-resend-activation-v2-email',
    },
  },
]

const footer = (
  <div className="text-center text-p-400 text-secondary">
    <Link to="/login" className={AUTH_LINK_CLASS} data-testid="link-back-to-login">
      Back to login
    </Link>
  </div>
)

/**
 * Standalone "Resend activation email" page for pending-activation self-signups
 * (SER-6348), reached from the login and signup footer links. The backend responds
 * identically whether or not an account exists, so the success copy is generic
 * and never confirms an account. All rate limiting is server-side (2/60s + 5/h
 * per email, 10/h per IP) — a 429 renders in the alert and the form stays live.
 */
export const ResendActivationV2 = () => {
  const { userAuthenticated } = useAuth()
  const recaptchaRef = useRef() as React.MutableRefObject<ReCAPTCHA>
  const {
    mutate: resend,
    isPending,
    error,
    isSuccess,
    data,
  } = useResendActivationEmailMutation(recaptchaRef)
  const defaultValueRef = useRef<ResendActivationFormData>({ email: '' })

  if (userAuthenticated) return <Navigate to={getDefaultRoute()} />

  if (isSuccess) {
    return (
      <AuthLayout
        pageTitle="Resend activation email"
        title="Check Your Email"
        description=""
        footer={footer}
      >
        <Card className="p-6 text-center">
          <p className="text-p-400" aria-live="polite" data-testid="resend-activation-success">
            {data?.message || GENERIC_SUCCESS_COPY}
          </p>
        </Card>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      pageTitle="Resend activation email"
      title="Resend activation email"
      description="Enter your registered email and we'll send you a new activation link."
      footer={footer}
    >
      {error && <Alert variant="destructive" content={getErrorText(error)} />}
      <GenericForm
        id="resend-activation-v2-form"
        defaultValues={defaultValueRef.current}
        fields={fields}
        onSubmit={(value) => resend(value)}
        className="grid gap-4"
        isSubmitting={isPending}
        includeSubmitButton={false}
      />
      <FormSubmitButton
        formId="resend-activation-v2-form"
        typography="action"
        className={AUTH_PRIMARY_BUTTON_CLASS}
        size="xl"
        loading={isPending}
        data-testid="button-resend-activation"
      >
        Resend Activation Email
      </FormSubmitButton>
      <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={RECAPTCHA_SITE_KEY} />
    </AuthLayout>
  )
}
